import { useEffect } from 'react';
import { Platform } from 'react-native';
import * as braze from '@braze/web-sdk';

const STORAGE_KEY = 'ld_ref';
const COOKIE_MAX_AGE_DAYS = 30;

// ─── Cookie helpers ────────────────────────────────────────────────────────────

function setCookie(value: string): void {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  // Secure flag only when already on HTTPS so localhost dev still works
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${STORAGE_KEY}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function getCookie(): string | null {
  const entry = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${STORAGE_KEY}=`));
  return entry ? decodeURIComponent(entry.split('=')[1]) : null;
}

function deleteCookie(): void {
  document.cookie = `${STORAGE_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
}

// ─── Layered storage: cookie → localStorage → sessionStorage ──────────────────
// Cookies survive browser close and are readable cross-tab.
// localStorage survives close but may be cleared in strict private modes.
// sessionStorage is the existing fallback for environments that block both.

function writeCode(code: string): void {
  try {
    setCookie(code);
  } catch {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      try {
        sessionStorage.setItem(STORAGE_KEY, code);
      } catch {
        // all storage blocked — tracking unavailable
      }
    }
  }
}

export function getStoredReferralCode(): string | null {
  try {
    const cookie = getCookie();
    if (cookie) return cookie;
  } catch { /* fall through */ }

  try {
    const local = localStorage.getItem(STORAGE_KEY);
    if (local) return local;
  } catch { /* fall through */ }

  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearStoredReferralCode(): void {
  try { deleteCookie(); } catch { /* ignore */ }
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export function useReferralCode() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    // Always use the most recent link (last-click attribution).
    // If no ref param is present the previously stored code is preserved.
    if (ref) {
      const existing = getStoredReferralCode();
      writeCode(ref);
      // Fire referral_click when code is new or has changed so Braze attributes
      // the click at capture time rather than waiting for authentication.
      if (Platform.OS === 'web' && ref !== existing) {
        braze.logCustomEvent('referral_click', { referralCode: ref });
      }
    }
  }, []);
}
